import React, { useState, useEffect } from 'react';
import { AgentUser, Role, CreateAgentRequest, UpdateAgentRequest } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import UserList from './UserList';
import CreateUserForm from './CreateUserForm';
import EditUserForm from './EditUserForm';
import RoleManagement from './RoleManagement';
import AuditTrail from './AuditTrail';
import SystemSettings from './SystemSettings';
import AiAgentSettings from './AiAgentSettings';
import SyncEvents from './SyncEvents';
import socketService from '../../services/socket';

interface UserManagementProps {}

const UserManagement: React.FC<UserManagementProps> = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'agents' | 'roles' | 'audit' | 'settings' | 'ai-agent' | 'sync-events'>('agents');
  const [agents, setAgents] = useState<AgentUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentUser | null>(null);
  const [agentPresence, setAgentPresence] = useState<Map<string, boolean>>(new Map());

  // Check if user has access to system management functions
  const hasSystemAccess = user?.permissions?.includes('system.management') ?? false;
  const hasUserManagementAccess = (user?.permissions?.includes('system.user_management') || user?.roleName === 'Admin') ?? false;
  const hasRoleManagementAccess = (user?.permissions?.includes('system.role_management') || user?.roleName === 'Admin') ?? false;
  const canCreateUsers = (user?.permissions?.includes('users.create') || user?.roleName === 'Admin') ?? false;
  const canEditUsers = (user?.permissions?.includes('users.edit') || user?.permissions?.includes('system.user_management') || user?.roleName === 'Admin') ?? false;
  const canDeleteUsers = (user?.permissions?.includes('users.delete') || user?.roleName === 'Admin') ?? false;
  const canViewAudit = (user?.permissions?.includes('audit.view') || user?.roleName === 'Admin') ?? false;
  const hasSystemSettings = (user?.permissions?.includes('system.settings') || user?.permissions?.includes('system.management') || user?.roleName === 'Admin') ?? false;
  const hasAiSettings = (user?.permissions?.includes('system.ai_settings') || user?.permissions?.includes('system.management') || user?.roleName === 'Admin') ?? false;
  const hasSyncEvents = (user?.permissions?.includes('system.management') || user?.roleName === 'Admin') ?? false;
  const isAdmin = user?.roleName === 'Admin';

  // Debug logging
  console.log('👤 User permissions check:', {
    user: user?.email,
    roleName: user?.roleName,
    permissions: user?.permissions,
    hasUserManagementAccess,
    canEditUsers,
    canDeleteUsers,
    isAdmin,
    hasSyncEvents,
    activeTab,
    hasSystemManagement: user?.permissions?.includes('system.management'),
    isAdminRole: user?.roleName === 'Admin'
  });

  // Debug tab rendering
  useEffect(() => {
    console.log('🔄 Tab rendering debug - hasSyncEvents:', hasSyncEvents, 'activeTab:', activeTab);
  }, [hasSyncEvents, activeTab]);



  useEffect(() => {
    if (hasSystemAccess || hasUserManagementAccess || isAdmin) {
      fetchAgents();
      // Fetch roles for user management (needed for agent editing)
      fetchRoles();
    }
  }, [hasSystemAccess, hasUserManagementAccess, hasRoleManagementAccess, isAdmin]);

  // Real-time agent status updates
  useEffect(() => {
    if (activeTab === 'agents') {
      // Listen for agent status changes
      const handleAgentStatusChange = (data: { agentId: string; isOnline: boolean; lastSeen: string }) => {
        console.log('👤 UserManagement: Agent status changed:', data);
        setAgentPresence(prev => {
          const updated = new Map(prev);
          updated.set(data.agentId, data.isOnline);
          return updated;
        });
        
        // Also update the agents list with real-time status
        setAgents(prev => prev.map(agent => 
          agent.id === data.agentId 
            ? { ...agent, isOnline: data.isOnline, lastSeen: data.lastSeen }
            : agent
        ));
      };

      // Listen for bulk agent status updates
      const handleAgentsOnlineStatusUpdate = (data: { onlineAgents: Array<{ agentId: string; isOnline: boolean; lastSeen: string; agentName: string }> }) => {
        console.log('👤 UserManagement: Bulk agent status update:', data);
        
        // Update agent presence map
        setAgentPresence(prev => {
          const updated = new Map(prev);
          
          // First mark all agents as offline
          prev.forEach((_, agentId) => {
            updated.set(agentId, false);
          });
          
          // Then mark online agents as online
          data.onlineAgents.forEach(agent => {
            updated.set(agent.agentId, agent.isOnline);
          });
          
          return updated;
        });
        
        // Update agents list
        setAgents(prev => prev.map(agent => {
          const onlineAgent = data.onlineAgents.find(oa => oa.agentId === agent.id);
          return onlineAgent 
            ? { ...agent, isOnline: onlineAgent.isOnline, lastSeen: onlineAgent.lastSeen }
            : { ...agent, isOnline: false };
        }));
      };

      // Set up socket event listeners
      socketService.on('agent_status_changed', handleAgentStatusChange);
      socketService.on('agents_online_status_update', handleAgentsOnlineStatusUpdate);

      return () => {
        socketService.off('agent_status_changed', handleAgentStatusChange);
        socketService.off('agents_online_status_update', handleAgentsOnlineStatusUpdate);
      };
    }
  }, [activeTab]);

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/agents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch agents');
      }

      const data = await response.json();
      console.log('Fetched agents data:', data);
      
      // The backend returns { success: true, data: { agents: [...] } }
      if (data.success && data.data && Array.isArray(data.data.agents)) {
        // Merge with current presence information
        const agentsWithPresence = data.data.agents.map((agent: AgentUser) => ({
          ...agent,
          isOnline: agent.isOnline || agentPresence.get(agent.id) || false,
        }));
        
        setAgents(agentsWithPresence);
        console.log('📊 UserManagement: Agents fetched with presence:', agentsWithPresence);
      } else {
        console.error('Expected agents array but got:', typeof data, data);
        setError('Invalid data format received from server');
        setAgents([]);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch agents');
      setAgents([]); // Ensure agents is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      // Use different endpoint based on user permissions
      const endpoint = hasRoleManagementAccess 
        ? `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/roles`  // Full roles data for role management
        : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/roles/simple`;  // Simple roles for user management

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      setRoles(data);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setRoles([]); // Ensure roles is always an array
    }
  };

  const handleCreateAgent = async (agentData: CreateAgentRequest) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/agents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(agentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create agent');
      }

      const newAgent = await response.json();
      setAgents([...agents, newAgent]);
      setShowCreateForm(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create agent');
    }
  };

  const handleUpdateAgent = async (id: string, updateData: UpdateAgentRequest) => {
    try {
      const token = localStorage.getItem('auth_token');
      console.log('🔄 Updating agent:', id, 'with data:', updateData);
      console.log('🔐 Using token:', token ? token.substring(0, 20) + '...' : 'No token');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/agents/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers));

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);
        
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication failed. Please log in again.');
        }
        
        throw new Error(errorData.message || `Failed to update agent (${response.status})`);
      }

      const updatedAgent = await response.json();
      console.log('✅ Agent updated successfully:', updatedAgent);
      
      setAgents(agents.map(agent => agent.id === id ? updatedAgent : agent));
      setEditingAgent(null);
      setError(null);
    } catch (err) {
      console.error('❌ Update agent error:', err);
      setError(err instanceof Error ? err.message : 'Failed to update agent');
    }
  };

  const handleDeleteAgent = async (id: string, permanent: boolean = false) => {
    if (!window.confirm(permanent ? 'Are you sure you want to permanently delete this agent?' : 'Are you sure you want to deactivate this agent?')) {
      return;
    }

    try {
      console.log('🗑️ Attempting to delete agent:', id, permanent ? '(permanent)' : '(deactivate)');
      const token = localStorage.getItem('auth_token');
      console.log('🔐 Using token:', token ? token.substring(0, 20) + '...' : 'No token');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/agents/${id}${permanent ? '?permanent=true' : ''}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 Delete response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Delete error response:', errorData);
        
        if (response.status === 403) {
          throw new Error(`Access denied: ${errorData.message}. Please check your permissions. Debug info: ${JSON.stringify(errorData.debug || {})}`);
        }
        
        throw new Error(errorData.message || `Failed to delete agent (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Delete successful:', result);

      if (permanent) {
        setAgents(agents.filter(agent => agent.id !== id));
      } else {
        setAgents(agents.map(agent => 
          agent.id === id ? { ...agent, isActive: false } : agent
        ));
      }
      setError(null);
    } catch (err) {
      console.error('❌ Delete agent error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
    }
  };

  const handleResetPassword = async (id: string, sendEmail: boolean = true) => {
    try {
      console.log('🔐 Attempting to reset password for agent:', id);
      const token = localStorage.getItem('auth_token');
      console.log('🔐 Using token:', token ? token.substring(0, 20) + '...' : 'No token');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/agents/${id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sendEmail }),
      });

      console.log('📡 Reset password response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Reset password error response:', errorData);
        
        if (response.status === 403) {
          throw new Error(`Access denied: ${errorData.message}. Please check your permissions. Debug info: ${JSON.stringify(errorData.debug || {})}`);
        }
        
        throw new Error(errorData.message || `Failed to reset password (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Reset password successful:', result);
      
      if (result.temporaryPassword) {
        alert(`Password reset successfully. Temporary password: ${result.temporaryPassword}`);
      } else {
        alert('Password reset email sent successfully');
      }
      
      setError(null);
    } catch (err) {
      console.error('❌ Reset password error:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    }
  };

  if (!hasSystemAccess && !hasUserManagementAccess && !isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to access user management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">System Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage agent accounts, roles, and system settings</p>
        </div>
        {activeTab === 'agents' && canCreateUsers && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Agent
          </button>
        )}
      </div>



      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {hasUserManagementAccess && (
            <button
              onClick={() => setActiveTab('agents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'agents'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              User Management
            </button>
          )}
          {hasRoleManagementAccess && (
            <button
              onClick={() => setActiveTab('roles')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'roles'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Role Management
            </button>
          )}
          {canViewAudit && (
            <button
              onClick={() => setActiveTab('audit')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Audit Trail
            </button>
          )}
          {hasSystemSettings && (
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              System Settings
            </button>
          )}
          {hasAiSettings && (
            <button
              onClick={() => setActiveTab('ai-agent')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'ai-agent'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              AI Agent Settings
            </button>
          )}
          {hasSyncEvents && (
            <button
              onClick={() => setActiveTab('sync-events')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sync-events'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Sync Events
            </button>
          )}
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
          <div className="font-bold">Error:</div>
          <div>{error}</div>
          {error.includes('Authentication failed') && (
            <div className="mt-2 text-sm">
              Please refresh the page and log in again. Your session may have expired.
            </div>
          )}
          {error.includes('Agent not found') && (
            <div className="mt-2 text-sm">
              The agent you're trying to edit may not exist or you don't have permission to edit it. Check the console for details.
            </div>
          )}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'agents' && hasUserManagementAccess && (
        <>
          {showCreateForm && (
            <CreateUserForm
              roles={roles}
              onSubmit={handleCreateAgent}
              onCancel={() => setShowCreateForm(false)}
            />
          )}

          {editingAgent && (
            <EditUserForm
              agent={editingAgent}
              roles={roles}
              onSubmit={(updateData: UpdateAgentRequest) => handleUpdateAgent(editingAgent.id, updateData)}
              onCancel={() => setEditingAgent(null)}
            />
          )}

          <UserList
            agents={agents}
            roles={roles}
            canEdit={canEditUsers}
            canDelete={canDeleteUsers}
            onEdit={setEditingAgent}
            onDelete={handleDeleteAgent}
            onResetPassword={handleResetPassword}
          />
        </>
      )}

      {activeTab === 'roles' && hasRoleManagementAccess && (
        <RoleManagement />
      )}

      {activeTab === 'audit' && canViewAudit && (
        <AuditTrail />
      )}

      {activeTab === 'settings' && hasSystemSettings && (
        <SystemSettings />
      )}

      {activeTab === 'ai-agent' && hasAiSettings && (
        <AiAgentSettings />
      )}

      {activeTab === 'sync-events' && hasSyncEvents && (
        <SyncEvents />
      )}
    </div>
  );
};

export default UserManagement; 